import { useState, useCallback, useRef, useEffect } from 'react';
import { COMMAND_CENTER_GATEWAY_ORGANIZATION, gatewayClient, normalizeSSEToMessage } from '../services/gateway';
import { agentService } from '../services/api/agent';
import { useGatewayConnection } from './useGatewayConnection';
import { resolveEquanautSessionKey } from '../services/gateway/session-resolver';
import type { UIMessage } from '../services/api/chat/types';
import type { PendingAction, ExecutionStep, AgentResponse, ChatMessageDto } from '../services/api/agent/types';
import type { ConnectionStatus, SSEEvent } from '../services/gateway';

export interface UseGatewayChatOptions {
  organizationId?: string;
  sessionKey?: string;
  taskId?: string;
  commandCenterMode?: boolean;
  onActionRequired?: (action: PendingAction) => void;
  onActionCompleted?: (action: PendingAction, confirmed: boolean) => void;
}

export interface UseGatewayChatReturn {
  messages: UIMessage[];
  isLoading: boolean;
  error: string | null;
  pendingAction: PendingAction | null;
  executingSteps: ExecutionStep[];
  conversationId: string | null;
  sendMessage: (content: string) => Promise<void>;
  confirmAction: (confirmed: boolean, confirmationCode?: string) => Promise<void>;
  clearMessages: () => void;
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>;
  dismissPendingAction: () => void;
  clearError: () => void;
  connectionStatus: ConnectionStatus;
  isGatewayConnected: boolean;
}

export function useGatewayChat(options: UseGatewayChatOptions): UseGatewayChatReturn {
  const {
    organizationId,
    sessionKey: explicitSessionKey,
    taskId,
    commandCenterMode,
    onActionRequired,
    onActionCompleted,
  } = options;

  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [executingSteps, setExecutingSteps] = useState<ExecutionStep[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [inFallback, setInFallback] = useState(false);

  const historyRef = useRef<ChatMessageDto[]>([]);
  const prevConnectedRef = useRef(false);
  const effectiveOrganizationId = organizationId || (commandCenterMode ? COMMAND_CENTER_GATEWAY_ORGANIZATION : undefined);

  // Internal gateway health tracking (5.1: detect availability)
  const gatewayConn = useGatewayConnection(organizationId, commandCenterMode);
  const isGatewayConnected = gatewayConn.isGatewayConnected;

  // 5.5: Auto-reconnect when gateway recovers
  useEffect(() => {
    if (isGatewayConnected && !prevConnectedRef.current && inFallback) {
      setInFallback(false);
      setMessages(prev => [
        ...prev,
        {
          id: `system-reconnect-${Date.now()}`,
          role: 'assistant',
          content: 'System: Reconnected to Gateway mode.',
          timestamp: Math.floor(Date.now() / 1000),
          source: 'gateway',
        },
      ]);
    }
    prevConnectedRef.current = isGatewayConnected;
  }, [isGatewayConnected, inFallback]);

  // Derive UI-facing connection status
  const connectionStatus: ConnectionStatus = (() => {
    if (inFallback) return 'fallback';
    if (isGatewayConnected) return 'connected';
    if (gatewayConn.retryCount > 0) return 'connecting';
    if (gatewayConn.error) return 'error';
    return 'disconnected';
  })();

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setPendingAction(null);
    setExecutingSteps([]);
    setConversationId(null);
    setInFallback(false);
    historyRef.current = [];
  }, []);

  const dismissPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fallback: send via equa-server direct agent chat when gateway is down.
   * Messages are tagged with source: 'fallback' per C4 clarification.
   */
  const sendViaFallback = useCallback(async (content: string) => {
    const directAgentAvailable = organizationId
      ? await agentService.checkAgentAvailable()
      : false;

    if (!organizationId || !directAgentAvailable) {
      const unavailableMessage = commandCenterMode
        ? 'I could not reach the Equanaut gateway response stream for that request. Command Center is wired through the gateway path; open Gateway Config to check the saved token and provider connection, then retry from Command Center.'
        : !organizationId
          ? 'No organization selected. Select an organization before sending a message.'
          : 'I could not reach the Equanaut gateway or direct agent response path for that request. Check the saved agent configuration and retry.';
      if (!commandCenterMode && !organizationId) {
        setError('No organization selected');
      }
      setMessages(prev => [
        ...prev,
        {
          id: `assistant-gateway-unavailable-${Date.now()}`,
          role: 'assistant',
          content: unavailableMessage,
          timestamp: Math.floor(Date.now() / 1000),
          source: 'fallback',
        },
      ]);
      historyRef.current.push({ role: 'assistant', content: unavailableMessage });
      setIsLoading(false);
      return;
    }

    if (!inFallback) {
      setMessages(prev => [
        ...prev,
        {
          id: `system-fallback-${Date.now()}`,
          role: 'assistant',
          content: 'System: Gateway unavailable. Switched to fallback mode.',
          timestamp: Math.floor(Date.now() / 1000),
          source: 'fallback',
        },
      ]);
    }
    setInFallback(true);
    try {
      const response: AgentResponse = await agentService.chat(
        organizationId,
        content,
        conversationId || undefined,
        historyRef.current.slice(0, -1),
      );

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      if (response.type === 'text' && response.message) {
        const msg: UIMessage = {
          id: `assistant-fallback-${Date.now()}`,
          role: 'assistant',
          content: response.message.content,
          timestamp: Math.floor(Date.now() / 1000),
          source: 'fallback',
        };
        setMessages(prev => [...prev, msg]);
        historyRef.current.push({ role: 'assistant', content: response.message.content });
        setConversationId(response.conversationId || conversationId);
      }

      if (response.type === 'confirmation_required' && response.pendingAction) {
        setPendingAction(response.pendingAction);
        onActionRequired?.(response.pendingAction);
      }

      if (response.executingSteps) {
        setExecutingSteps(response.executingSteps);
      }

      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || 'Fallback also failed');
      setIsLoading(false);
      historyRef.current.pop();
    }
  }, [organizationId, conversationId, onActionRequired, inFallback, commandCenterMode]);

  const sendMessage = useCallback(async (content: string) => {
    setIsLoading(true);
    setError(null);
    setPendingAction(null);
    setExecutingSteps([]);

    const userMessage: UIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Math.floor(Date.now() / 1000),
    };
    setMessages(prev => [...prev, userMessage]);
    historyRef.current.push({ role: 'user', content });

    // 5.1: Route to fallback when health check says gateway is down
    if (!isGatewayConnected || inFallback || !effectiveOrganizationId) {
      return sendViaFallback(content);
    }

    const sessionKey = resolveEquanautSessionKey(
      effectiveOrganizationId,
      commandCenterMode ? 'command-center' : taskId ? 'task' : 'equanaut',
      taskId,
      explicitSessionKey,
    );

    try {
      let gotResponse = false;

      await gatewayClient.chatStream(
        {
          organization: effectiveOrganizationId,
          message: content,
          sessionKey,
          taskId,
          history: historyRef.current.slice(0, -1),
        },
        {
          onEvent: (event: SSEEvent) => {
            switch (event.event) {
              case 'response': {
                gotResponse = true;
                const msg = normalizeSSEToMessage(event);
                if (msg) {
                  const taggedMsg = { ...msg, source: 'gateway' as const };
                  setMessages(prev => [...prev, taggedMsg]);
                  historyRef.current.push({ role: 'assistant', content: msg.content });
                  setConversationId(event.data?.metadata?.sessionKey || sessionKey || null);
                }
                setIsLoading(false);
                setExecutingSteps([]);
                break;
              }
              case 'done':
                setIsLoading(false);
                break;
              case 'error':
                setError(event.data?.error || 'Stream error');
                setIsLoading(false);
                break;
            }
          },
          onError: (err: Error) => {
            if (!gotResponse) {
              // 5.1: Gateway failed before any response -- fall back
              sendViaFallback(content);
              return;
            }
            setError(err.message || 'Failed to send message');
            setIsLoading(false);
            historyRef.current.pop();
          },
          onDone: () => {
            setIsLoading(false);
          },
        },
      );
    } catch {
      // 5.1: Connection failure -- fall back
      sendViaFallback(content);
    }
  }, [effectiveOrganizationId, isGatewayConnected, inFallback, explicitSessionKey, taskId, commandCenterMode, sendViaFallback]);

  const confirmAction = useCallback(async (confirmed: boolean, confirmationCode?: string) => {
    if (!pendingAction) {
      setError('No action to confirm');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (inFallback) {
        const response = await agentService.confirm(
          pendingAction.id,
          confirmed,
          confirmationCode,
        );
        onActionCompleted?.(pendingAction, confirmed);
        setPendingAction(null);
        if (response?.type === 'text' && response?.message) {
          const confirmedMessage = response.message;
          setMessages(prev => [...prev, {
            id: `assistant-${Date.now()}`,
            role: 'assistant' as const,
            content: confirmedMessage.content,
            timestamp: Math.floor(Date.now() / 1000),
            source: 'fallback' as const,
          }]);
        }
        setIsLoading(false);
        return;
      }

      if (!effectiveOrganizationId) {
        setError('No organization selected for gateway action confirmation');
        setIsLoading(false);
        return;
      }

      const result = await gatewayClient.invokeTool({
        organization: effectiveOrganizationId,
        toolName: 'confirm_action',
        args: { stepId: pendingAction.id, confirmed, confirmationCode },
      });

      onActionCompleted?.(pendingAction, confirmed);
      setPendingAction(null);

      if (result?.message) {
        setMessages(prev => [...prev, {
          id: `assistant-${Date.now()}`,
          role: 'assistant' as const,
          content: typeof result.message === 'string' ? result.message : result.message.content,
          timestamp: Math.floor(Date.now() / 1000),
          source: 'gateway' as const,
        }]);
      }
      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm action');
      setIsLoading(false);
    }
  }, [pendingAction, effectiveOrganizationId, inFallback, onActionCompleted]);

  return {
    messages,
    isLoading,
    error,
    pendingAction,
    executingSteps,
    conversationId,
    sendMessage,
    confirmAction,
    clearMessages,
    setMessages,
    dismissPendingAction,
    clearError,
    connectionStatus,
    isGatewayConnected,
  };
}

export default useGatewayChat;
